
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getSeatMaps } from '@/api/functions';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';

// Shared layout constants so the letter header, seats, and placeholders all
// align into the same fixed column grid (industry-standard seat map look).
const SEAT_CELL = 'w-8 h-9 md:w-10 md:h-11';
const GUTTER = 'w-5 md:w-6 flex-shrink-0';
const AISLE = 'w-5 md:w-9 flex-shrink-0';
const SEAT_GAP = 'gap-0.5 md:gap-1.5';

const seatLetter = (designator) => String(designator || '').replace(/^\d+/, '');

// Non-seat cabin elements Duffel reports, rendered as labeled blocks so the
// map reads like a real cabin diagram.
const FACILITY_LABELS = {
  lavatory: 'WC',
  galley: 'GALLEY',
  stairs: 'STAIRS',
  closet: 'CLOSET',
  bassinet: 'BASSINET',
};

/**
 * Builds the column model for a cabin: one ordered list of seat letters per
 * section (sections are separated by an aisle). Rows missing a seat in a
 * column render an empty placeholder, so seats always align vertically.
 */
const buildSectionColumns = (cabin) => {
  const sections = [];
  cabin.rows.forEach((row) => {
    row.sections.forEach((section, sIdx) => {
      if (!sections[sIdx]) sections[sIdx] = new Set();
      section.elements.forEach((el) => {
        // Anything with a designator occupies a seat cell — including
        // restricted_seat_general elements (blocked seats Duffel can't sell).
        if (el.designator) {
          const letter = seatLetter(el.designator);
          if (letter) sections[sIdx].add(letter);
        }
      });
    });
  });
  return sections.map((set) => [...set].sort());
};

const FACILITY_FRIENDLY = {
  lavatory: 'lavatory',
  galley: 'galley',
  stairs: 'stairs',
  closet: 'closet',
  bassinet: 'bassinet',
};

/**
 * Derived context for the seat detail popover: where the row sits within the
 * cabin (front/middle/rear) and which facilities are in or adjacent to the
 * row. Duffel's per-seat payload is just designator + price + disclosures —
 * everything else useful has to be derived from the cabin layout.
 */
const buildSeatContext = (cabin, seatId) => {
  const rows = cabin?.rows || [];
  const rowNumber = parseInt(String(seatId).match(/\d+/)?.[0], 10);

  const rowNums = new Set();
  let seatRowIdx = -1;
  rows.forEach((row, idx) => {
    (row.sections || []).forEach((sec) => (sec.elements || []).forEach((el) => {
      if (el.designator === seatId) seatRowIdx = idx;
      const n = parseInt(String(el.designator || '').match(/\d+/)?.[0], 10);
      if (Number.isFinite(n)) rowNums.add(n);
    }));
  });

  const sorted = [...rowNums].sort((a, b) => a - b);
  const rowIdx = sorted.indexOf(rowNumber);
  let zone = null;
  if (rowIdx !== -1 && sorted.length > 1) {
    const frac = rowIdx / (sorted.length - 1);
    zone = frac <= 1 / 3 ? 'Front of cabin' : frac >= 2 / 3 ? 'Rear of cabin' : 'Middle of cabin';
  }

  const nearby = new Set();
  if (seatRowIdx !== -1) {
    [seatRowIdx - 1, seatRowIdx, seatRowIdx + 1].forEach((i) => {
      const row = rows[i];
      if (!row) return;
      (row.sections || []).forEach((sec) => (sec.elements || []).forEach((el) => {
        if (!el.designator && FACILITY_FRIENDLY[el.type]) nearby.add(FACILITY_FRIENDLY[el.type]);
      }));
    });
  }

  return {
    rowNumber: Number.isFinite(rowNumber) ? rowNumber : null,
    totalRows: sorted.length || null,
    zone,
    nearby: [...nearby],
  };
};

// --- Seat detail popover, anchored beside the just-selected seat ---
// Rendered through a portal with fixed positioning so the map's scroll
// container can't clip it; tracks the seat across scroll/resize.
const POPOVER_WIDTH = 172;

const SeatDetailPopover = ({ anchorEl, info, onClose }) => {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!anchorEl) return undefined;
    const update = () => {
      const r = anchorEl.getBoundingClientRect();
      const placeRight = r.right + 10 + POPOVER_WIDTH <= window.innerWidth - 8;
      const left = placeRight ? r.right + 10 : Math.max(8, r.left - 10 - POPOVER_WIDTH);
      const top = Math.min(Math.max(8, r.top + r.height / 2 - 70), window.innerHeight - 180);
      setPos({ left, top, side: placeRight ? 'right' : 'left', anchorY: r.top + r.height / 2 });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorEl]);

  if (!pos) return null;

  const arrowTop = Math.min(Math.max(pos.anchorY - pos.top - 4, 10), 150);
  const priceLabel = Number.isNaN(info.price) || info.price === 0
    ? 'Free'
    : `$${info.price.toFixed(2).replace(/\.00$/, '')}`;

  return createPortal(
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.18 }}
      style={{ position: 'fixed', left: pos.left, top: pos.top, width: POPOVER_WIDTH, zIndex: 70 }}
      className="rounded-xl border border-brand-400/30 bg-[#0C101D]/95 backdrop-blur-md shadow-2xl p-3 cursor-pointer select-none"
      onClick={onClose}
      title="Tap to dismiss"
    >
      <span
        style={{ top: arrowTop }}
        className={`absolute w-2 h-2 rotate-45 bg-[#0C101D] ${
          pos.side === 'right'
            ? '-left-[5px] border-l border-b border-brand-400/30'
            : '-right-[5px] border-r border-t border-brand-400/30'
        }`}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-white font-mono font-semibold text-sm">{info.designator}</span>
        <span className="text-brand-300 text-xs font-medium">{priceLabel}</span>
      </div>
      {info.position && (
        <p className="text-white/75 text-xs mt-1 capitalize">
          {info.position} seat
        </p>
      )}
      {(info.zone || info.rowNumber) && (
        <p className="text-white/50 text-[10px] mt-0.5">
          {info.rowNumber && info.totalRows ? `Row ${info.rowNumber} of ${info.totalRows}` : null}
          {info.rowNumber && info.totalRows && info.zone ? ' • ' : ''}
          {info.zone || ''}
        </p>
      )}
      {info.nearby?.length > 0 && (
        <p className="text-white/50 text-[10px] mt-0.5">Near {info.nearby.join(' & ')}</p>
      )}
      <div className="flex flex-wrap gap-1 mt-1.5">
        {info.cabinLabel && (
          <span className="px-1.5 py-0.5 rounded bg-white/[0.07] border border-white/10 text-[9px] uppercase tracking-wider text-white/55">
            {info.cabinLabel}
          </span>
        )}
        {info.isExitRow && (
          <span className="px-1.5 py-0.5 rounded bg-red-500/15 border border-red-400/30 text-[9px] uppercase tracking-wider text-red-300">
            Exit row
          </span>
        )}
      </div>
      <p className="text-white/45 text-[10px] mt-1.5 truncate">{info.passengerName}</p>
      {info.disclosures.length > 0 && (
        <p className="text-white/40 text-[10px] leading-snug mt-1.5 pt-1.5 border-t border-white/10 line-clamp-3">
          {info.disclosures.join(' • ')}
        </p>
      )}
    </motion.div>,
    document.body
  );
};

// --- Seat Component ---
const Seat = ({ seat, cabin, onSelect, isSelected, isOccupied, badgeLabel, isExitSeat }) => {
  const isAvailable = seat.available_services?.length > 0;
  const isDisabled = isOccupied || !isAvailable;

  const price = isAvailable ? parseFloat(seat.available_services[0].total_amount) : NaN;
  const isPaid = !Number.isNaN(price) && price > 0;
  const priceLabel = !isAvailable || Number.isNaN(price)
    ? null
    : (price === 0 ? 'Free' : `$${Math.round(price)}`);

  const seatColorClasses = () => {
    if (isSelected) return 'bg-brand-500 border-brand-300 text-white shadow-[0_0_12px_rgba(10, 18, 51,0.45)]';
    if (isDisabled) return 'bg-white/[0.03] border-white/10 text-white/25 cursor-not-allowed';
    if (isPaid) return 'bg-brand-300/35 border-brand-200/70 text-white hover:bg-brand-300/50 hover:border-brand-100 cursor-pointer';
    return 'bg-brand-500/15 border-brand-400/40 text-brand-100 hover:bg-brand-400/30 hover:border-brand-300 cursor-pointer';
  };

  const disclosures = Array.isArray(seat.disclosures) && seat.disclosures.length > 0
    ? ` • ${seat.disclosures.join(' • ')}`
    : '';
  const exitNote = isExitSeat ? ' • Exit row' : '';

  return (
    <motion.button
      type="button"
      whileTap={{ scale: isDisabled ? 1 : 0.9 }}
      title={isDisabled
        ? `${seat.designator} — Unavailable${exitNote}`
        : `${seat.designator} — ${priceLabel}${exitNote}${disclosures}`}
      className={`relative ${SEAT_CELL} rounded-t-lg rounded-b-[4px] flex flex-col items-center justify-center transition-colors duration-150 border ${seatColorClasses()} ${
        isExitSeat && !isSelected ? 'ring-1 ring-red-400/60' : ''
      }`}
      onClick={(e) => !isDisabled && onSelect(seat, cabin, e.currentTarget)}
      disabled={isDisabled}
    >
      <span className="text-[10px] font-mono leading-none">{isOccupied ? '✕' : seat.designator}</span>
      {!isDisabled && priceLabel && (
        <span className="text-[8px] leading-none mt-0.5 opacity-70">{priceLabel}</span>
      )}
      {isSelected && (
        <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-white text-brand-600 rounded-full flex items-center justify-center text-[10px] font-semibold shadow">
          {badgeLabel}
        </div>
      )}
    </motion.button>
  );
};

// --- Column letter header (A B C  |aisle|  D E F) ---
const ColumnHeader = ({ sectionColumns }) => (
  <div className="flex items-center">
    <span className={GUTTER} />
    {sectionColumns.map((letters, sIdx) => (
      <React.Fragment key={sIdx}>
        {sIdx > 0 && <span className={AISLE} />}
        <div className={`flex ${SEAT_GAP}`}>
          {letters.map((letter) => (
            <span key={letter} className="w-8 md:w-10 text-center text-[10px] text-white/40 font-mono">
              {letter}
            </span>
          ))}
        </div>
      </React.Fragment>
    ))}
    <span className={GUTTER} />
  </div>
);

// --- Row Component ---
const Row = ({ row, cabin, sectionColumns, onSelect, selectedSeats, occupiedSeats, passengerCount, isWingRow }) => {
  const seatsByKey = {};
  let hasSeats = false;
  let isExitRow = false;
  const facilitiesBySection = row.sections.map(() => []);

  row.sections.forEach((section, sIdx) => {
    section.elements.forEach((el) => {
      if (el.designator) {
        // Seats and restricted/blocked seat variants both occupy a cell
        seatsByKey[`${sIdx}:${seatLetter(el.designator)}`] = el;
        hasSeats = true;
      } else if (el.type === 'exit_row') {
        isExitRow = true;
      } else if (FACILITY_LABELS[el.type]) {
        facilitiesBySection[sIdx].push(FACILITY_LABELS[el.type]);
      }
    });
  });

  // Duffel rows carry no row-number field; derive it from the seat designators.
  const rowNumber = (() => {
    const first = Object.values(seatsByKey)[0];
    const match = String(first?.designator || '').match(/^\d+/);
    return match ? match[0] : '';
  })();

  // Wing position bars hug the fuselage edges; -bottom bridges the row gap so
  // contiguous wing rows read as one continuous strip.
  const wingBars = isWingRow ? (
    <>
      <span className="absolute left-0 -top-0.5 -bottom-1.5 w-[5px] rounded-sm bg-gradient-to-r from-white/25 to-transparent pointer-events-none" />
      <span className="absolute right-0 -top-0.5 -bottom-1.5 w-[5px] rounded-sm bg-gradient-to-l from-white/25 to-transparent pointer-events-none" />
    </>
  ) : null;

  // Seatless rows: galleys, lavatories, exit doors, stairs, closets.
  if (!hasSeats) {
    const hasFacilities = facilitiesBySection.some((f) => f.length > 0);
    if (!hasFacilities && !isExitRow) {
      return <div className="relative h-2.5 w-full">{wingBars}</div>;
    }
    return (
      <div className="relative flex items-center w-full py-0.5">
        {wingBars}
        <span className={`${GUTTER} text-center text-[8px] md:text-[9px] font-semibold tracking-wider text-red-400`}>
          {isExitRow ? 'EXIT' : ''}
        </span>
        {sectionColumns.map((letters, sIdx) => (
          <React.Fragment key={sIdx}>
            {sIdx > 0 && <span className={AISLE} />}
            <div className={`relative flex ${SEAT_GAP}`}>
              {letters.map((letter) => (
                <span key={letter} className="w-8 md:w-10 h-7" />
              ))}
              <div className="absolute inset-0 flex items-center justify-center gap-1 px-0.5">
                {facilitiesBySection[sIdx].length > 0 ? (
                  facilitiesBySection[sIdx].map((label, i) => (
                    <span
                      key={i}
                      className="flex-1 min-w-0 truncate text-center px-1.5 py-1 rounded-md bg-white/[0.05] border border-white/10 text-[8px] font-medium tracking-[0.15em] text-white/45"
                    >
                      {label}
                    </span>
                  ))
                ) : isExitRow ? (
                  <span className="w-full border-t border-dashed border-red-400/30" />
                ) : null}
              </div>
            </div>
          </React.Fragment>
        ))}
        <span className={`${GUTTER} text-center text-[8px] md:text-[9px] font-semibold tracking-wider text-red-400`}>
          {isExitRow ? 'EXIT' : ''}
        </span>
      </div>
    );
  }

  return (
    <div className="relative flex items-center">
      {wingBars}
      <span className={`${GUTTER} text-center font-mono ${isExitRow ? 'text-[8px] md:text-[10px] text-red-400 font-medium' : 'text-[10px] text-white/35'}`}>
        {isExitRow ? 'EXIT' : rowNumber}
      </span>
      {sectionColumns.map((letters, sIdx) => (
        <React.Fragment key={sIdx}>
          {sIdx > 0 && <span className={AISLE} />}
          <div className={`flex ${SEAT_GAP}`}>
            {letters.map((letter, colIdx) => {
              const el = seatsByKey[`${sIdx}:${letter}`];
              if (!el) return <span key={letter} className={SEAT_CELL} />;
              const selection = selectedSeats[el.designator];
              // Window = outer edge of outer sections; aisle = any edge facing
              // an aisle gap; everything else is a middle seat.
              const isFirstCol = colIdx === 0;
              const isLastCol = colIdx === letters.length - 1;
              const lastSection = sectionColumns.length - 1;
              const position =
                (sIdx === 0 && isFirstCol) || (sIdx === lastSection && isLastCol)
                  ? 'Window'
                  : isFirstCol || isLastCol
                    ? 'Aisle'
                    : 'Middle';
              return (
                <Seat
                  key={el.designator}
                  seat={el}
                  cabin={cabin}
                  onSelect={(seat, seatCabin, anchorEl) => onSelect(seat, seatCabin, { position, isExitRow, anchorEl })}
                  isSelected={Boolean(selection)}
                  isOccupied={occupiedSeats.has(el.designator)}
                  isExitSeat={isExitRow}
                  badgeLabel={passengerCount > 1 && selection ? String((selection.passengerId ?? 0) + 1) : '✓'}
                />
              );
            })}
          </div>
        </React.Fragment>
      ))}
      <span className={`${GUTTER} text-center font-mono ${isExitRow ? 'text-[8px] md:text-[10px] text-red-400 font-medium' : 'text-[10px] text-white/35'}`}>
        {isExitRow ? 'EXIT' : rowNumber}
      </span>
    </div>
  );
};

// --- Main SeatMap Component ---
export default function SeatMap({ flightOffer, segment, passengers, onSeatsSelected, onBack = null, initialSelections = {}, preloadedSeatMapData = null, continueLabel = 'Continue to Next Flight Segment', requireAllPassengers = true }) {
  const [seatMap, setSeatMap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState(initialSelections);
  const [occupiedSeats, setOccupiedSeats] = useState(new Set()); 
  const [currentPassengerIndex, setCurrentPassengerIndex] = useState(0);
  const [lastSelected, setLastSelected] = useState(null); // detail overlay for the most recent pick

  // Reset selections when segment changes
  useEffect(() => {
    setSelectedSeats(initialSelections);
    setCurrentPassengerIndex(0);
    setLastSelected(null);
  }, [segment.id, initialSelections]);

  useEffect(() => {
    const fetchSeatMap = async () => {
      setIsLoading(true);
      setError(null);
      setSeatMap(null);

      try {
        // Caller may already have the seat maps (e.g. SelectSeats pre-fetches
        // them while matching the flight on Duffel) — skip the network call.
        let seatMapList;
        if (Array.isArray(preloadedSeatMapData) && preloadedSeatMapData.length > 0) {
          seatMapList = preloadedSeatMapData;
        } else {
          const { data: responseData, status } = await getSeatMaps({ offer_id: flightOffer.id });

          if (status !== 200 || !responseData.data) {
            throw new Error(responseData.errors?.[0]?.message || 'Failed to fetch seat maps from the airline.');
          }
          seatMapList = responseData.data;
        }

        // IMPROVED MATCHING: Try multiple approaches to find the correct seat map
        let currentSegmentSeatMap = null;

        // Approach 1: Try direct segment ID matching
        currentSegmentSeatMap = seatMapList.find(sm => sm.segment_id === segment.id);

        // Approach 2: If no match by segment ID, try matching by origin/destination
        if (!currentSegmentSeatMap) {
          currentSegmentSeatMap = seatMapList.find(sm => {
            // Find a matching segment in the seat map data
            return sm.slice_index !== undefined && 
                   flightOffer.slices[sm.slice_index]?.segments.find(seg => 
                     seg.origin.iata_code === segment.origin.iata_code &&
                     seg.destination.iata_code === segment.destination.iata_code
                   );
          });
        }

        // Approach 3: If still no match, try to find any seat map that matches origin/destination
        if (!currentSegmentSeatMap && seatMapList.length > 0) {
          // Look for any seat map that could correspond to this route
          for (const seatMapData of seatMapList) {
            // Check if any of the flight's segments match this route
            // The original intent was to find a slice containing a matching segment.
            // flightOffer.slices is an array of slice objects, so .flat() is not appropriate here.
            const matchingSlice = flightOffer.slices.find(slice => 
              slice.segments && slice.segments.find(seg =>
                seg.origin.iata_code === segment.origin.iata_code &&
                seg.destination.iata_code === segment.destination.iata_code
              )
            );
            
            if (matchingSlice) {
              currentSegmentSeatMap = seatMapData;
              break;
            }
          }
        }

        // Approach 4: As a last resort, if there's only one seat map, use it
        if (!currentSegmentSeatMap && seatMapList.length === 1) {
          console.warn(`Using the only available seat map for ${segment.origin.iata_code} → ${segment.destination.iata_code}`);
          currentSegmentSeatMap = seatMapList[0];
        }

        if (!currentSegmentSeatMap) {
          throw new Error(`No seat map available for ${segment.origin.iata_code} → ${segment.destination.iata_code}. This flight may not offer seat selection.`);
        }
        
        setSeatMap(currentSegmentSeatMap);

        const newOccupiedSeats = new Set();
        currentSegmentSeatMap.cabins.forEach(cabin => {
          cabin.rows.forEach(row => {
            row.sections.forEach(section => {
              section.elements.forEach(element => {
                // Mark as occupied: taken seats (no services) and restricted /
                // blocked seat variants (e.g. restricted_seat_general)
                if (element.designator && (element.type !== 'seat' || !element.available_services?.length)) {
                  newOccupiedSeats.add(element.designator);
                }
              });
            });
          });
        });
        setOccupiedSeats(newOccupiedSeats);

      } catch (err) {
        console.error("Seat map fetch error:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (flightOffer && segment) {
      fetchSeatMap();
    }
  }, [flightOffer, segment, preloadedSeatMapData]); // Removed state setters from dependency array as they are stable

  const handleSeatSelect = (seat, cabin, meta = {}) => {
    const newSelectedSeats = { ...selectedSeats };
    const seatId = seat.designator;
    const currentPassenger = passengers[currentPassengerIndex];

    // Check if the current passenger already has a seat selected for THIS segment
    const existingSeatForPassenger = Object.keys(newSelectedSeats).find(
      key => newSelectedSeats[key].passengerId === currentPassengerIndex && newSelectedSeats[key].segmentId === segment.id
    );

    if (existingSeatForPassenger) {
      // If the passenger clicks their own seat, deselect it.
      if (existingSeatForPassenger === seatId) {
        delete newSelectedSeats[seatId];
        setSelectedSeats(newSelectedSeats);
        setLastSelected(null);
        // Do not move to next passenger, as they might re-select
        return; 
      }
      // If passenger clicks a new seat, deselect the old one first, only if it belongs to the current segment.
      if (newSelectedSeats[existingSeatForPassenger].segmentId === segment.id) {
        delete newSelectedSeats[existingSeatForPassenger];
      }
    }
    
    // Add the new seat selection
    newSelectedSeats[seatId] = {
      passengerId: currentPassengerIndex,
      passenger: currentPassenger,
      price: {
        amount: seat.available_services[0].total_amount,
        currency: seat.available_services[0].total_currency,
      },
      cabin: { name: cabin.name, class: cabin.cabin_class },
      position: meta.position,
      segmentId: segment.id // Add segment ID to selection
    };
    setSelectedSeats(newSelectedSeats);
    const seatContext = buildSeatContext(cabin, seatId);
    setLastSelected({
      anchorEl: meta.anchorEl || null,
      designator: seatId,
      price: parseFloat(seat.available_services[0].total_amount),
      currency: seat.available_services[0].total_currency,
      cabinLabel: cabin.name || String(cabin.cabin_class || '').replace(/_/g, ' '),
      position: meta.position,
      isExitRow: Boolean(meta.isExitRow),
      disclosures: Array.isArray(seat.disclosures) ? seat.disclosures : [],
      seatName: seat.name || null,
      ...seatContext,
      passengerIndex: currentPassengerIndex,
      passengerName: currentPassenger?.first_name || currentPassenger?.last_name
        ? `${currentPassenger?.first_name || ''} ${currentPassenger?.last_name || ''}`.trim()
        : `Passenger ${currentPassengerIndex + 1}`,
    });
    
    // Move to the next passenger who doesn't have a seat for this segment
    const nextPassengerIndex = passengers.findIndex((p, index) => 
        !Object.values(newSelectedSeats).some(s => s.passengerId === index && s.segmentId === segment.id)
    );
  
    if (nextPassengerIndex !== -1) {
        setCurrentPassengerIndex(nextPassengerIndex);
    } else {
        // If all passengers have seats, stay on the last passenger, or set to 0 if no passengers
        setCurrentPassengerIndex(passengers.length > 0 ? passengers.length - 1 : 0);
    }
  };

  const handleProceedToNextSegment = () => {
    // Pass the selected seats back to the parent to handle progression
    onSeatsSelected(selectedSeats);
  };

  if (isLoading) {
    return (
      <div className="text-center py-16 glass rounded-xl">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary mb-4" />
        <p className="text-white">Loading Seat Map...</p>
        <p className="text-gray-400 text-sm">Please wait while we fetch the cabin layout.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 glass rounded-xl bg-red-900/20 border border-red-500/30">
        <AlertTriangle className="w-8 h-8 mx-auto text-red-400 mb-4" />
        <p className="text-red-300 font-semibold">Error Loading Seat Map</p>
        <p className="text-red-400 text-sm max-w-md mx-auto">{error}</p>
        <Button onClick={() => onSeatsSelected({})} className="mt-4">Skip Seat Selection for this Flight</Button>
      </div>
    );
  }

  if (!seatMap) {
    return null;
  }
  
  const currentPassenger = passengers[currentPassengerIndex];
  const numUnseatedPassengers = passengers.filter((p, index) => 
    !Object.values(selectedSeats).some(s => s.passengerId === index && s.segmentId === segment.id)
  ).length;

  const allPassengersHaveSeats = passengers.length > 0 && passengers.every((_, index) => 
    Object.values(selectedSeats).some(seatData => seatData.passengerId === index && seatData.segmentId === segment.id)
  );
  const seatedCount = passengers.length - numUnseatedPassengers;
  // Checkout requires everyone seated before moving on; the dashboard
  // purchase flow lets a customer buy seats for just some travelers (or
  // none on this segment) and still progress.
  const canContinue = requireAllPassengers ? allPassengersHaveSeats : true;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="glass rounded-xl p-3 md:p-6">
        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-3 md:gap-4 mb-5 md:mb-6">
          <div className="min-w-0 flex-shrink-0">
            <h3 className="text-base md:text-lg font-bold text-white whitespace-nowrap truncate">
              Select a seat for: <span className="text-primary-foreground">
                {currentPassenger?.first_name || currentPassenger?.last_name
                  ? `${currentPassenger?.first_name || ''} ${currentPassenger?.last_name || ''}`.trim()
                  : `Passenger ${currentPassengerIndex + 1}`}
              </span>
            </h3>
            <p className="text-gray-400 text-xs md:text-sm">
              {passengers.length > 1
                ? `${passengers.length - numUnseatedPassengers} of ${passengers.length} passengers seated`
                : 'Tap an available seat to choose it'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 md:gap-x-4 gap-y-1.5 text-xs md:text-sm">
            <div className="flex items-center gap-1.5 md:gap-2"><div className="w-3.5 h-3.5 md:w-4 md:h-4 rounded-t-md rounded-b-[3px] bg-brand-500/15 border border-brand-400/40"></div><span className="text-gray-400">Included</span></div>
            <div className="flex items-center gap-1.5 md:gap-2"><div className="w-3.5 h-3.5 md:w-4 md:h-4 rounded-t-md rounded-b-[3px] bg-brand-300/35 border border-brand-200/70"></div><span className="text-gray-400">Preferred</span></div>
            <div className="flex items-center gap-1.5 md:gap-2"><div className="w-3.5 h-3.5 md:w-4 md:h-4 rounded-t-md rounded-b-[3px] bg-brand-500 border border-brand-300"></div><span className="text-gray-400">Selected</span></div>
            <div className="flex items-center gap-1.5 md:gap-2"><div className="w-3.5 h-3.5 md:w-4 md:h-4 rounded-t-md rounded-b-[3px] bg-white/[0.03] border border-white/10 flex items-center justify-center text-white/25 text-[9px]">✕</div><span className="text-gray-400">Occupied</span></div>
            <div className="flex items-center gap-1.5 md:gap-2"><div className="w-3.5 h-3.5 md:w-4 md:h-4 rounded-t-md rounded-b-[3px] bg-brand-500/15 border border-brand-400/40 ring-1 ring-red-400/60"></div><span className="text-gray-400">Exit row</span></div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="inline-flex min-w-full justify-center">
            <div className="space-y-3">
              {seatMap.cabins.map((cabin, cabinIndex) => {
                const sectionColumns = buildSectionColumns(cabin);
                const wings = cabin.wings;
                const isWingRow = (idx) =>
                  wings != null &&
                  wings.first_row_index != null &&
                  idx >= wings.first_row_index &&
                  idx <= wings.last_row_index;
                return (
                  <div
                    key={cabinIndex}
                    className={`px-2 py-4 md:px-5 bg-white/[0.03] border border-white/10 ${
                      cabinIndex === 0 ? 'rounded-t-[3rem] rounded-b-2xl pt-7' : 'rounded-2xl'
                    } ${cabinIndex === seatMap.cabins.length - 1 ? 'rounded-b-[2rem]' : ''}`}
                  >
                    {(cabin.name || cabin.cabin_class) && (
                      <h4 className="text-center text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">
                        {cabin.name || String(cabin.cabin_class).replace(/_/g, ' ')}
                      </h4>
                    )}
                    <div className="flex flex-col items-center gap-1 md:gap-1.5">
                      <ColumnHeader sectionColumns={sectionColumns} />
                      {cabin.rows.map((row, rowIndex) => (
                        <Row
                          key={rowIndex}
                          row={row}
                          cabin={cabin}
                          sectionColumns={sectionColumns}
                          onSelect={handleSeatSelect}
                          selectedSeats={selectedSeats}
                          occupiedSeats={occupiedSeats}
                          passengerCount={passengers.length}
                          isWingRow={isWingRow(rowIndex)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Floating detail popover beside the just-selected seat */}
        <AnimatePresence>
          {lastSelected?.anchorEl && (
            <SeatDetailPopover
              key={lastSelected.designator}
              anchorEl={lastSelected.anchorEl}
              info={lastSelected}
              onClose={() => setLastSelected(null)}
            />
          )}
        </AnimatePresence>

        {/* Footer: back to previous segment + continue. The continue button is
            always visible so customers are never stranded — disabled with a
            hint until the seating requirement is met. */}
        <div className="mt-6 pt-4 border-t border-white/10">
          {/* Stacked full-width on mobile (long labels overflow side-by-side),
              row on md+. flex-col-reverse keeps the primary Continue button on
              top for thumbs while staying right-aligned on desktop. */}
          <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-2.5 md:gap-3">
            {onBack ? (
              <Button
                onClick={onBack}
                className="w-full md:w-auto bg-[var(--accent-color)] hover:bg-primary/90 text-white hover-glow"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Previous Flight Segment
              </Button>
            ) : (
              <span className="hidden md:block" />
            )}
            <Button 
              onClick={handleProceedToNextSegment}
              disabled={!canContinue}
              className="w-full md:w-auto bg-[var(--accent-color)] hover:bg-primary/90 text-white hover-glow disabled:opacity-40"
            >
              {!requireAllPassengers && seatedCount === 0 ? 'Skip Seats for this Flight' : continueLabel}
            </Button>
          </div>
          {!canContinue && (
            <p className="text-center md:text-right text-xs text-white/60 mt-2">
              Select a seat for {passengers.length > 1 ? 'each passenger' : 'your passenger'} to continue
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
